import type { EventHandlers } from "./client";
import { ContractClient, type RequestOptions, toQuery } from "./contractClient";
import { ApiError } from "./errors";
import { LOGIN_PATH, OPERATIONS, type OperationId, fillPath } from "./operations";
import type { AppEvent, Me } from "./types";

/**
 * 本物の API につなぐ実装（同じオリジンの /api/v1、Cookie のセッション、変更には X-CSRF-Token）。
 * 開発中は Vite が /api と /auth をバックエンドへ中継し、本番は API が frontend/dist を同じオリジンで配信する（contracts/README.md）。
 */
export class HttpApi extends ContractClient {
  readonly mode = "http" as const;
  private csrf = "";
  private onUnauthorized: () => void;

  constructor(opts: { onUnauthorized?: () => void } = {}) {
    super();
    this.onUnauthorized = opts.onUnauthorized ?? (() => {});
  }

  loginUrl(): string | null {
    // ログインの入口はページ遷移で、OpenAPI には載らない（contracts/README.md）。/me が契約にあれば使える
    return this.isConnected("me") ? LOGIN_PATH : null;
  }

  protected onMe(me: Me): void {
    this.csrf = me.csrf_token;
  }

  async logout() {
    await super.logout();
    this.csrf = "";
  }

  protected async send<T>(op: OperationId, opts: RequestOptions): Promise<T> {
    const [method, path] = OPERATIONS[op];
    const url = fillPath(path, opts.params) + toQuery(opts.query);
    const headers: Record<string, string> = { accept: "application/json" };
    if (method !== "GET") {
      headers["X-CSRF-Token"] = this.csrf;
      if (opts.body !== undefined) headers["content-type"] = "application/json";
    }
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers,
        credentials: "same-origin",
        cache: "no-store",
        body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      });
    } catch {
      throw new ApiError(0, "network");
    }
    if (res.status === 204) return undefined as T;
    let data: unknown = null;
    const text = await res.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }
    }
    if (!res.ok) {
      const err = (
        data as { error?: { code?: string; message?: string; correlation_id?: string | null; details?: Record<string, unknown> } } | null
      )?.error;
      if (res.status === 401) this.onUnauthorized();
      const code = err?.code ?? (res.status === 422 ? "validation_error" : `http_${res.status}`);
      throw new ApiError(res.status, code, err?.message, err?.correlation_id ?? undefined, err?.details);
    }
    return data as T;
  }

  subscribe(handlers: EventHandlers): () => void {
    if (!this.isConnected("events")) {
      queueMicrotask(handlers.onError);
      return () => {};
    }
    const es = new EventSource(OPERATIONS.events[1], { withCredentials: true });
    let closed = false;
    es.onopen = () => !closed && handlers.onOpen();
    const onData = (msg: MessageEvent) => {
      try {
        handlers.onEvent(JSON.parse(msg.data) as AppEvent);
      } catch {
        // 読めない通知は無視する（正本はスナップショット）
      }
    };
    es.onmessage = onData;
    // 名前つきのイベント（event: invalidate など）で届いても受け取る
    for (const name of ["snapshot", "invalidate", "access_revoked"]) es.addEventListener(name, onData as EventListener);
    es.onerror = () => {
      // EventSource は自分でつなぎ直そうとするが、つなぎ直したあとは必ず /me・/playback・/queue を取り直したいので、
      // ここで閉じて、呼び出し側（src/state/sync.ts）の手順でつなぎ直す
      if (closed) return;
      closed = true;
      es.close();
      handlers.onError();
    };
    return () => {
      closed = true;
      es.close();
    };
  }
}
